import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Facebook, Instagram, Building2, Home, MapPin, AlertCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/ui/toast/use-toast';
import BaseEditProfile, { EditSection, EditProfileImage } from '../BaseEditProfile';

// Constantes para URLs de Cloudflare
const CLOUDFLARE_PROFILES_URL = 'https://pub-2a10bd66114a4441b95e9249adee01cf.r2.dev';
const CLOUDFLARE_BANNERS_URL = 'https://pub-cdaec3d9feef44cf8950eee9a93266e4.r2.dev';

// Función mejorada para procesar imágenes de perfil
const getProfileImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  // Caso especial: cuando la URL contiene tanto localhost como la URL de Cloudflare
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/profiles/https://pub-') && imageUrl.includes('r2.dev')) {
    // Extraer solo la parte de Cloudflare
    return imageUrl.substring(imageUrl.indexOf('https://pub-'));
  }
  
  // Si ya es una URL de Cloudflare, usarla directamente
  if (imageUrl.includes('r2.dev')) {
    return imageUrl;
  }
  
  // Si es una URL local completa del servidor
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/profiles/')) {
    const filename = imageUrl.split('/profiles/').pop();
    return `${CLOUDFLARE_PROFILES_URL}/${filename}`;
  }
  
  // Si es una ruta relativa con /profiles/
  if (imageUrl.includes('/profiles/')) {
    const filename = imageUrl.split('/profiles/').pop();
    return `${CLOUDFLARE_PROFILES_URL}/${filename}`;
  }
  
  // Si es solo el nombre del archivo
  return `${CLOUDFLARE_PROFILES_URL}/${imageUrl}`;
};

// Función mejorada para procesar banners
const getBannerImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  // Caso especial: cuando la URL contiene tanto localhost como la URL de Cloudflare
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/banners/https://pub-') && imageUrl.includes('r2.dev')) {
    // Extraer solo la parte de Cloudflare
    return imageUrl.substring(imageUrl.indexOf('https://pub-'));
  }
  
  // Si ya es una URL de Cloudflare, usarla directamente
  if (imageUrl.includes('r2.dev')) {
    return imageUrl;
  }
  
  // Si es una URL local completa del servidor
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/banners/')) {
    const filename = imageUrl.split('/banners/').pop();
    return `${CLOUDFLARE_BANNERS_URL}/${filename}`;
  }
  
  // Si es una ruta relativa con /banners/
  if (imageUrl.includes('/banners/')) {
    const filename = imageUrl.split('/banners/').pop();
    return `${CLOUDFLARE_BANNERS_URL}/${filename}`;
  }
  
  // Si es solo el nombre del archivo
  return `${CLOUDFLARE_BANNERS_URL}/${imageUrl}`;
};

const EditClientProfile = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  
  // Estados
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [profileData, setProfileData] = useState({
    nombre: '',
    sobre_mi: '',
    facebook: '',
    instagram: '',
    ubicacion: '',
    preferencias: {
      tipo_propiedad: [],
      presupuesto_min: '',
      presupuesto_max: '',
      dormitorios: '',
      banos: '',
      area_min: '',
      area_max: ''
    }
  });

  // Estados para imágenes
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  // Cargar datos del perfil
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        if (!token) {
          throw new Error('No hay token de autenticación');
        }

        const response = await fetch('https://backend-production-1786.up.railway.app/api/vendedores/perfil', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Error response:', errorText);
          throw new Error(errorText || 'Error al cargar el perfil');
        }

        const data = await response.json();
        console.log('Profile data:', data);

        // Formatear datos para cliente
        const clientData = {
          nombre: data.nombre,
          sobre_mi: data.sobre_mi || '',
          facebook: data.facebook || '',
          instagram: data.instagram || '',
          ubicacion: data.ubicacion || '',
          preferencias: data.preferencias ? 
            (typeof data.preferencias === 'string' 
              ? JSON.parse(data.preferencias) 
              : data.preferencias) 
            : {
              tipo_propiedad: [],
              presupuesto_min: '',
              presupuesto_max: '',
              dormitorios: '',
              banos: '',
              area_min: '',
              area_max: ''
            }
        };

        setProfileData(clientData);

        // Procesar URLs de imágenes correctamente
        if (data.banner_image) {
          const bannerUrl = getBannerImageUrl(data.banner_image);
          console.log('URL de banner procesada:', bannerUrl);
          setBannerPreview(bannerUrl);
        }
  
        if (data.foto_perfil) {
          const profileUrl = getProfileImageUrl(data.foto_perfil);
          console.log('URL de imagen de perfil procesada:', profileUrl);
          setProfileImagePreview(profileUrl);
        }
        
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError(error.message);
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [token, toast]);

  // Manejadores de cambios
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
    setHasUnsavedChanges(true);
  };

  const handlePreferenceChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      preferencias: {
        ...prev.preferencias,
        [name]: value
      }
    }));
    setHasUnsavedChanges(true);
  };

  const handlePropertyTypeToggle = (tipo) => {
    setProfileData(prev => ({
      ...prev,
      preferencias: {
        ...prev.preferencias,
        tipo_propiedad: prev.preferencias.tipo_propiedad.includes(tipo)
          ? prev.preferencias.tipo_propiedad.filter(t => t !== tipo)
          : [...prev.preferencias.tipo_propiedad, tipo]
      }
    }));
    setHasUnsavedChanges(true);
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
      setHasUnsavedChanges(true);
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImageFile(file);
      setProfileImagePreview(URL.createObjectURL(file));
      setHasUnsavedChanges(true);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const formData = new FormData();

      // Agregar archivos si se han cambiado
      if (bannerFile) formData.append('banner', bannerFile);
      if (profileImageFile) formData.append('profile_image', profileImageFile);

      // Agregar resto de datos
      Object.keys(profileData).forEach(key => {
        if (key === 'preferencias') {
          formData.append(key, JSON.stringify(profileData[key]));
        } else {
          formData.append(key, profileData[key]);
        }
      });

      const response = await fetch('https://backend-production-1786.up.railway.app/api/vendedores/perfil', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Error al guardar los cambios');
      }

      toast({
        title: "Éxito",
        description: "Perfil actualizado correctamente",
        variant: "success"
      });

      setHasUnsavedChanges(false);
      navigate('/mi_perfil');
    } catch (error) {
      console.error('Error saving profile:', error);
      setError(error.message);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Error handling en caso de que no se pueda cargar el perfil
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-red-50 p-4">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error al Cargar Perfil</h2>
          <p className="text-gray-700 mb-6">{error}</p>
          <div className="flex justify-center space-x-4">
            <button 
              onClick={() => window.location.reload()}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition"
            >
              Reintentar
            </button>
            <button 
              onClick={() => navigate('/mi_perfil')}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 transition"
            >
              Volver al Perfil
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <BaseEditProfile
      loading={loading}
      error={error}
      bannerPreview={bannerPreview}
      onBannerChange={handleBannerChange}
      onSave={handleSave}
      onCancel={() => navigate('/mi_perfil')}
    >
      {/* Profile Info Section */}
      <EditSection title="Información Personal">
        <div className="flex items-start gap-8">
          <EditProfileImage
            src={profileImagePreview}
            onChange={handleProfileImageChange}
          />
          
          <div className="flex-1 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nombre
              </label>
              <input
                type="text"
                name="nombre"
                value={profileData.nombre}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ubicación
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="ubicacion"
                  value={profileData.ubicacion}
                  onChange={handleInputChange}
                  className="w-full pl-10 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Ej: Piura, Perú"
                />
              </div>
            </div>
          </div>
        </div>
      </EditSection>

      {/* Social Media Section */}
      <EditSection title="Redes Sociales">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex items-center gap-4">
            <Facebook className="w-6 h-6 text-blue-500" />
            <input
              type="text"
              name="facebook"
              value={profileData.facebook}
              onChange={handleInputChange}
              placeholder="Usuario de Facebook"
              className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center gap-4">
            <Instagram className="w-6 h-6 text-pink-500" />
            <input
              type="text"
              name="instagram"
              value={profileData.instagram}
              onChange={handleInputChange}
              placeholder="Usuario de Instagram"
              className="flex-1 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </EditSection>

      {/* About Section */}
      <EditSection title="Sobre Mí">
        <textarea
          name="sobre_mi"
          value={profileData.sobre_mi}
          onChange={handleInputChange}
          placeholder="Cuéntanos un poco sobre ti..."
          className="w-full p-4 border rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[150px]"
        />
      </EditSection>

      {/* Preferences Section */}
      <EditSection title="Preferencias de Búsqueda">
        <div className="space-y-6">
          {/* Property Types */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipos de Propiedad
            </label>
            <div className="flex flex-wrap gap-3">
              {[
                { id: 'casa', label: 'Casa', icon: Home },
                { id: 'departamento', label: 'Departamento', icon: Building2 },
                { id: 'terreno', label: 'Terreno', icon: MapPin }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => handlePropertyTypeToggle(id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors
                           ${profileData.preferencias.tipo_propiedad.includes(id)
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Budget Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Presupuesto Mínimo (S/)
              </label>
              <input
                type="number"
                name="presupuesto_min"
                value={profileData.preferencias.presupuesto_min}
                onChange={handlePreferenceChange}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Presupuesto Máximo (S/)
              </label>
              <input
                type="number"
                name="presupuesto_max"
                value={profileData.preferencias.presupuesto_max}
                onChange={handlePreferenceChange}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Property Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dormitorios
              </label>
              <select
                name="dormitorios"
                value={profileData.preferencias.dormitorios}
                onChange={handlePreferenceChange}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Sin preferencia</option>
                {[1, 2, 3, 4, 5].map(num => (
                  <option key={num} value={num}>{num}+</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Baños
              </label>
              <select
                name="banos"
                value={profileData.preferencias.banos}
                onChange={handlePreferenceChange}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Sin preferencia</option>
                {[1, 2, 3, 4].map(num => (
                  <option key={num} value={num}>{num}+</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Área (m²)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  name="area_min"
                  value={profileData.preferencias.area_min}
                  onChange={handlePreferenceChange}
                  placeholder="Min"
                  className="w-1/2 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="number"
                  name="area_max"
                  value={profileData.preferencias.area_max}
                  onChange={handlePreferenceChange}
                  placeholder="Max"
                  className="w-1/2 p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>
      </EditSection>
    </BaseEditProfile>
  );
};

export default EditClientProfile;